import React, { useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext.hook';
import { AppRole } from '@/contexts/auth-context-base';
import { getRoleRedirect, resolvePrimaryRole } from '@/permissions';
import { LumaSpin } from '@/components/ui/luma-spin';
import { TermsGate } from '@/components/terms';
import { IncorporadorOnboardingGate } from '@/components/incorporador/IncorporadorOnboardingGate';
import { SuitabilityGate } from '@/components/suitability/SuitabilityGate';
import { KycGate } from '@/components/kyc/KycGate';
import { MfaChallengeGate } from '@/components/security/MfaChallengeGate';
import { useAccessLogger } from '@/features/admin/hooks/useAccessLogger';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: AppRole;
  allowedRoles?: AppRole[];
}

  // Resolução de role primária centralizada em src/permissions/

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRole,
  allowedRoles,
}) => {
  const { user, userRoles, loading, primaryRole, profile } = useAuth();
  // Registra acesso para QUALQUER role autenticada (independente de gate).
  // Antes ficava só dentro de TermsGate, mas investidor/incorporador passam por
  // SuitabilityGate/IncorporadorOnboardingGate e não eram registrados.
  useAccessLogger();
  const [loadingTimeoutReached, setLoadingTimeoutReached] = useState(false);

  useEffect(() => {
    if (loading) {
      const timeout = setTimeout(() => {
        console.warn('[ProtectedRoute] Loading timeout reached, proceeding with fallback');
        setLoadingTimeoutReached(true);
      }, 15000); // 15 seconds timeout

      return () => clearTimeout(timeout);
    } else {
      setLoadingTimeoutReached(false);
    }
  }, [loading]);

  // Show loading screen only if actually loading and timeout not reached
  if (loading && !loadingTimeoutReached) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-hero">
        <LumaSpin size="lg" />
      </div>
    );
  }

  // If timeout reached but still no user, redirect to login
  if (loadingTimeoutReached && !user) {
    console.warn('[ProtectedRoute] Timeout reached with no user, redirecting to login');
    return <Navigate to="/" replace />;
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  const userRoleNames = userRoles.map(role => role.role);
  const effectiveRole = primaryRole ?? resolvePrimaryRole(userRoleNames) ?? 'investidor';
  const roleSet = new Set<AppRole>([...userRoleNames, effectiveRole]);

  const redirectForRole = (role: AppRole) => getRoleRedirect(role);

  if (requiredRole && !roleSet.has(requiredRole)) {
    return <Navigate to={redirectForRole(effectiveRole)} replace />;
  }

  if (allowedRoles && !allowedRoles.some(role => roleSet.has(role))) {
    return <Navigate to={redirectForRole(effectiveRole)} replace />;
  }

  // Investidor com onboarding incompleto → redirecionar para /onboarding
  // Statuses que indicam onboarding suficiente para acessar o app:
  // - 'completed': fluxo completo
  // - 'password_set' / 'terms_accepted': investidores legados migrados (SuitabilityGate cuida do resto)
  if (effectiveRole === 'investidor') {
    const status = profile?.onboarding_status;
    const onboardingDone = status === 'completed' || status === 'password_set' || status === 'terms_accepted';
    if (!status || !onboardingDone) {
      return <Navigate to="/onboarding" replace />;
    }
  }

  // Incorporadores usam onboarding dedicado (tela cheia) em vez de gates modais
  if (effectiveRole === 'incorporador') {
    return (
      <MfaChallengeGate>
        <IncorporadorOnboardingGate>
          {children}
        </IncorporadorOnboardingGate>
      </MfaChallengeGate>
    );
  }

  // Investidores aceitam termos durante o signup (Step 3) — porém o sistema
  // dinâmico Phase 8/9 permite admin publicar novas versões dos Termos de
  // Uso a qualquer momento. Quando isso acontece, TODOS os usuários (incluindo
  // investidores legados) precisam re-aceitar a nova versão antes de acessar
  // a aplicação. Por isso o TermsGate envolve também a rota de investidor.
  // SuitabilityGate roda DEPOIS do TermsGate. MfaChallengeGate roda ANTES de
  // tudo — fecha o gap de TOTPSettings.tsx (enroll sem enforcement no login).
  if (effectiveRole === 'investidor') {
    return (
      <MfaChallengeGate>
        <TermsGate>
          <SuitabilityGate>{children}</SuitabilityGate>
        </TermsGate>
      </MfaChallengeGate>
    );
  }

  // Demais roles passam pelo TermsGate normalmente
  return (
    <MfaChallengeGate>
      <TermsGate>
        {children}
      </TermsGate>
    </MfaChallengeGate>
  );
};

